import { putOSS } from '@/utils/uploadOSS.js';

import stampAgree from '@/assets/report/stamp_agree.svg';
import stampReject from '@/assets/report/stamp_reject.svg';
import stampProcess from '@/assets/report/stamp_process.svg';
import stampClose from '@/assets/report/stamp_close.svg';

// 解析接口返回的文件字段
export function parseReportFiles(file) {
    if (!file) {
        return [];
    }
    if (Array.isArray(file)) {
        return file.map((item) => {
            if (typeof item === 'string') {
                return item;
            }
            return item.url || item.path || item.file || '';
        }).filter(Boolean);
    }
    if (typeof file === 'string') {
        const trimmed = file.trim();
        if (!trimmed || trimmed === '""' || trimmed === '[]') {
            return [];
        }
        try {
            const parsed = JSON.parse(trimmed);
            return parseReportFiles(parsed);
        } catch (err) {
            return trimmed.indexOf('http') === 0 ? [trimmed] : [];
        }
    }
    if (typeof file === 'object') {
        const values = Object.keys(file)
            .sort((a, b) => Number(a) - Number(b))
            .map((key) => file[key]);
        return values.map((item) => {
            if (typeof item === 'string') {
                return item;
            }
            if (item && typeof item === 'object') {
                return item.url || item.path || item.file || '';
            }
            return '';
        }).filter(Boolean);
    }
    return [];
}

export function formatReportFiles(urls) {
    const validUrls = (urls || []).filter((url) => typeof url === 'string' && url.trim());
    if (!validUrls.length) {
        return '';
    }
    return JSON.stringify(validUrls.map((url) => ({ url })));
}

// 上传本地图片（支持 File / van-uploader 对象 / 已上传 url）
export function uploadReportImages(localFiles) {
    const tasks = (localFiles || []).map((item) => {
        if (typeof item === 'string') {
            if (/^https?:\/\//.test(item)) {
                return Promise.resolve(item);
            }
            return Promise.reject(new Error('无效图片'));
        }
        if (item && typeof item === 'object') {
            if (item.url && /^https?:\/\//.test(item.url) && !item.file) {
                return Promise.resolve(item.url);
            }
            const file = item.file || item;
            if (file instanceof File || file instanceof Blob) {
                return putOSS(file);
            }
        }
        if (item instanceof File || item instanceof Blob) {
            return putOSS(item);
        }
        return Promise.reject(new Error('无效图片'));
    });
    return Promise.all(tasks);
}

export function resolveReportTypeLabel(typeValue, typeMap) {
    if (!typeValue) {
        return '';
    }
    if (typeMap && typeMap[typeValue]) {
        return typeMap[typeValue];
    }
    return typeValue;
}

function buildTypeMap(typeOptions) {
    const map = {};
    (typeOptions || []).forEach((item) => {
        if (item && item.value) {
            map[item.value] = item.label || item.value;
        }
    });
    return map;
}

export function normalizeReportTypes(res) {
    const data = res && res.data;
    let list = [];
    if (Array.isArray(data)) {
        list = data;
    } else if (data && Array.isArray(data.list)) {
        list = data.list;
    } else if (data && Array.isArray(data.type)) {
        list = data.type;
    } else if (data && Array.isArray(data.data)) {
        list = data.data;
    } else if (data && typeof data === 'object') {
        list = Object.keys(data).map((key) => ({
            value: key,
            label: data[key]
        }));
    }
    return list.map((item) => {
        if (typeof item === 'string') {
            return { label: item, value: item };
        }
        const label = item.label || item.name || item.title || item.text || item.type_name || '';
        const value = item.type || item.code || item.key || item.value || item.id || '';
        return { label: label || value, value };
    }).filter((item) => item.value);
}

function getReportContentList(item) {
    const content = item && item.content;
    if (!Array.isArray(content)) {
        return [];
    }
    return [...content].sort((a, b) => {
        const timeA = new Date(a.create_time || 0).getTime();
        const timeB = new Date(b.create_time || 0).getTime();
        if (timeA !== timeB) {
            return timeA - timeB;
        }
        return Number(a.id || 0) - Number(b.id || 0);
    });
}

function getMessageSender(item) {
    const user = (item && item.user) || {};
    const relate = user.relate || {};
    const relateType = Number(user.relate_type);
    const isDriver = relateType === 2;
    const name = relate.name || relate.nick || relate.account || '';
    const displayName = name || (isDriver ? '我' : '调度员');
    return {
        isDriver,
        role: isDriver ? 'me' : 'dispatcher',
        roleName: isDriver ? '我' : displayName,
        userName: isDriver ? '我' : displayName,
        roleLabel: isDriver ? '我' : '调度',
        userId: user.id || user.relate_id || '',
        avatarText: isDriver ? '我' : (displayName ? displayName.charAt(0) : '调')
    };
}

const SCALAR_TYPE_MAP = {
    add_basement_rescue: (val) => `地库${val}层`,
    add_wheel: (val) => `小轮${val}个`,
    price_negotiation: (val) => `${val}元`
};

export function getScalarDisplay(typeValue, scalar) {
    if (scalar === undefined || scalar === null || scalar === '') {
        return null;
    }
    const formatter = SCALAR_TYPE_MAP[typeValue];
    if (!formatter) {
        return null;
    }
    return {
        text: formatter(scalar),
        showAgree: typeValue === 'price_negotiation'
    };
}

function mapContentMessage(item) {
    const sender = getMessageSender(item);
    return {
        id: item.id,
        contentId: item.id,
        role: sender.role,
        roleName: sender.roleName,
        userName: sender.userName,
        roleLabel: sender.roleLabel,
        userId: sender.userId,
        avatarText: sender.avatarText,
        isDriver: sender.isDriver,
        content: item.content || '',
        images: parseReportFiles(item.file),
        extra: item.extra || null,
        time: item.create_time || ''
    };
}

export const REPORT_STATUS_MAP = {
    1: { label: '同意', className: 'stamp_agree', stampImg: stampAgree },
    2: { label: '拒绝', className: 'stamp_reject', stampImg: stampReject },
    3: { label: '处理', className: 'stamp_process', stampImg: stampProcess },
    4: { label: '关闭', className: 'stamp_close', stampImg: stampClose }
};

export function getReportStatusStamp(status) {
    const key = Number(status);
    return REPORT_STATUS_MAP[key] || null;
}

function unwrapRawItem(item) {
    if (!item) {
        return null;
    }
    if (Array.isArray(item.content)) {
        return item;
    }
    if (item.raw && Array.isArray(item.raw.content)) {
        return item.raw;
    }
    return item;
}

function mapReportListItem(item, typeMap) {
    const rawItem = unwrapRawItem(item) || item;
    const contentList = getReportContentList(rawItem);
    const firstMsg = contentList[0];
    const typeValue = rawItem.type || rawItem.type_value || '';
    const messages = contentList.map(mapContentMessage);
    return {
        id: rawItem.id || rawItem.report_id,
        type: resolveReportTypeLabel(typeValue, typeMap) || typeValue,
        typeValue,
        time: rawItem.create_time || (firstMsg && firstMsg.create_time) || '',
        remark: firstMsg ? (firstMsg.content || '') : '',
        images: firstMsg ? parseReportFiles(firstMsg.file) : [],
        messages,
        replies: messages.slice(1),
        extra: rawItem.extra || null,
        status: rawItem.status,
        is_done: rawItem.is_done,
        result: rawItem.result || null,
        unread: Number(rawItem.unread) || 0,
        raw: rawItem
    };
}

function pickReportItem(data, fallback, targetId) {
    const id = targetId || (fallback && (fallback.id || fallback.report_id));
    if (!data) {
        return unwrapRawItem(fallback);
    }
    let list = null;
    if (Array.isArray(data)) {
        list = data;
    } else if (data && Array.isArray(data.list)) {
        list = data.list;
    } else if (data && Array.isArray(data.data)) {
        list = data.data;
    }
    if (Array.isArray(list)) {
        if (id) {
            return list.find((item) => item.id == id) || list[0] || unwrapRawItem(fallback);
        }
        return list[0] || unwrapRawItem(fallback);
    }
    if (typeof data === 'object' && (data.id || data.report_id)) {
        return data;
    }
    return unwrapRawItem(fallback);
}

export function normalizeReportList(res, typeOptions) {
    const typeMap = buildTypeMap(typeOptions);
    const data = res && res.data;
    if (Array.isArray(data)) {
        return data.map((item) => mapReportListItem(item, typeMap));
    }
    if (data && Array.isArray(data.list)) {
        return data.list.map((item) => mapReportListItem(item, typeMap));
    }
    if (data && Array.isArray(data.data)) {
        return data.data.map((item) => mapReportListItem(item, typeMap));
    }
    if (data && typeof data === 'object' && data.id) {
        return [mapReportListItem(data, typeMap)];
    }
    return [];
}

export function normalizeReportDetail(res, fallback, typeOptions) {
    const typeMap = buildTypeMap(typeOptions);
    const fallbackRaw = unwrapRawItem(fallback);
    const targetId = fallbackRaw && (fallbackRaw.id || fallbackRaw.report_id);
    const item = pickReportItem(res && res.data, fallbackRaw, targetId);
    if (!item) {
        return fallback && fallback.messages ? fallback : (fallback || {});
    }
    const normalized = mapReportListItem(item, typeMap);
    if (!normalized.messages.length && fallback && Array.isArray(fallback.messages) && fallback.messages.length) {
        return {
            ...fallback,
            ...normalized,
            messages: fallback.messages,
            replies: fallback.replies,
            remark: fallback.remark,
            images: fallback.images
        };
    }
    return normalized;
}
